import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the thresholdBackgroundPlugin locally (not globally)
const thresholdBackgroundPlugin = {
    id: 'thresholdBackground',
    beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) return;
        
        const { top, bottom, left, right, width } = chartArea;
        const { y } = scales;
        
        if (!y) return;
        
        // Get thresholds from the chart's options
        const thresholds = chart.options.plugins.thresholds || {};
        if (!thresholds.low && !thresholds.normalLow && !thresholds.normalHigh && !thresholds.high) return;
        
        // Get min and max values from the scale
        const minValue = y.min;
        const maxValue = y.max;
        
        // We draw from bottom to top
        // Very Low Zone (Red) - from min to low
        if (thresholds.low !== undefined) {
            const lowY = y.getPixelForValue(thresholds.low);
            ctx.fillStyle = 'rgba(255, 138, 138, 0.9)'; // Red with opacity
            ctx.fillRect(left, lowY, width, bottom - lowY);
        }
        
        // Low Zone (Yellow) - from low to normalLow
        if (thresholds.low !== undefined && thresholds.normalLow !== undefined) {
            const lowY = y.getPixelForValue(thresholds.low);
            const normalLowY = y.getPixelForValue(thresholds.normalLow);
            ctx.fillStyle = 'rgba(255, 229, 102, 0.4)'; // Yellow with opacity
            ctx.fillRect(left, normalLowY, width, lowY - normalLowY);
        }
        
        // Normal Zone (Green) - from normalLow to normalHigh
        if (thresholds.normalLow !== undefined && thresholds.normalHigh !== undefined) {
            const normalLowY = y.getPixelForValue(thresholds.normalLow);
            const normalHighY = y.getPixelForValue(thresholds.normalHigh);
            ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with opacity
            ctx.fillRect(left, normalHighY, width, normalLowY - normalHighY);
        }
        
        // High Zone (can be colored as needed)
        if (thresholds.normalHigh !== undefined && thresholds.high !== undefined) {
            const normalHighY = y.getPixelForValue(thresholds.normalHigh);
            const highY = y.getPixelForValue(thresholds.high);
            // Using green here but can be customized
            ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with opacity
            ctx.fillRect(left, highY, width, normalHighY - highY);
        }
        
        // Very High Zone (if needed)
        if (thresholds.high !== undefined) {
            const highY = y.getPixelForValue(thresholds.high);
            // Using green here but can be customized
            ctx.fillStyle = 'rgba(144, 255, 140, 0.8)'; // Green with opacity
            ctx.fillRect(left, top, width, highY - top);
        }
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(left, top, width, bottom - top);
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    annotationPlugin,
    thresholdBackgroundPlugin
);

// Updated getBarColor function for any range with threshold-based coloring
const getBarColor = (value, thresholds, zeroValue = false) => {
    // // If it's a zero value or invalid, return a light gray color
    // if (zeroValue || value === 0 || isNaN(value)) {
    //     return 'rgba(180, 180, 180, 0.5)'; // light gray for zero/missing values
    // }

    // if (thresholds.low !== undefined && value <= thresholds.low) return '#FF8A8A'; // Red (very low)
    // if (thresholds.normalLow !== undefined && value <= thresholds.normalLow) return '#FFE566'; // Yellow (low)
    // if (thresholds.normalHigh !== undefined && value <= thresholds.normalHigh) return '#90FF8C'; // Green (normal)
    // Return light gray for all values
    return 'rgba(200, 200, 200, 0.7)';
};

const ModularTrendsSpecificThreshholdChart = ({ 
    title, 
    metricLabel, 
    historyData, 
    thresholds = {}, 
    minValue, 
    maxValue,
    decimalPlaces = 1, // New prop for formatting values with appropriate decimal places
    dateFormat = 'short', // Control date format  
}) => {
    // Format date for x-axis labels
    const formatDateLabel = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.warn('Invalid date:', dateString);
                return '';
            }
            
            const weekday = date.toLocaleDateString('en-US', { weekday: dateFormat === 'short' ? 'short' : 'long' });
            const monthDay = date.toLocaleDateString('en-US', {
                month: dateFormat === 'short' ? 'short' : 'long',
                day: 'numeric'
            });
            
            return `${weekday}\n${monthDay}`;
        } catch (error) {
            console.error('Date parsing error:', error);
            return '';
        }
    };

    // Extract values and dates from historyData
    const processedData = React.useMemo(() => {
        if (!Array.isArray(historyData)) return { values: [], dates: [] };

        // Process data and then reverse both arrays to show oldest to newest
        const processed = historyData.reduce((acc, item) => {
            let value = typeof item === 'object' ? parseFloat(item.value) : parseFloat(item);
            let date = typeof item === 'object' ? item.period_start : null;
            
            // Handle time format like "8h 30m" for sleep data
            if (typeof item === 'object' && item.value && typeof item.value === 'string') {
                const timeMatch = item.value.match(/^(\d{1,2})h\s(\d{1,2})m$/);
                if (timeMatch) {
                    // Convert hours and minutes to total minutes for the chart
                    const hours = parseInt(timeMatch[1], 10);
                    const minutes = parseInt(timeMatch[2], 10);
                    value = hours * 60 + minutes; // Convert to total minutes
                }
            }
            
            acc.values.push(isNaN(value) ? 0 : value);
            acc.dates.push(date ? formatDateLabel(date) : '');

            return acc;
        }, { values: [], dates: [] });

        // Reverse both arrays to show oldest to newest (left to right)
        return {
            values: processed.values.reverse(),
            dates: processed.dates.reverse()
        };
    }, [historyData, dateFormat]);

    // Determine if a value is zero for coloring purposes
    const zeroValues = processedData.values.map(value => value === 0);

    // For sleep data, check if we have time format values
    const isSleepData = historyData.some(item => 
        typeof item === 'object' && 
        item.value && 
        typeof item.value === 'string' && 
        item.value.match(/^(\d{1,2})h\s(\d{1,2})m$/)
    );

    // Ensure all threshold values are numbers - without hardcoded defaults
    const validThresholds = {
        low: thresholds?.low !== undefined ? parseFloat(thresholds.low) : undefined,
        normalLow: thresholds?.normalLow !== undefined ? parseFloat(thresholds.normalLow) : undefined,
        average: thresholds?.average !== undefined ? parseFloat(thresholds.average) : undefined,
        normalHigh: thresholds?.normalHigh !== undefined ? parseFloat(thresholds.normalHigh) : undefined,
        high: thresholds?.high !== undefined ? parseFloat(thresholds.high) : undefined,
        status: thresholds?.status || ''
    };

    // Calculate full range for thresholds to ensure all zones are visible
    const getFullThresholdRange = () => {
        // Find actual min and max values in the data
        const values = processedData.values.filter(val => !isNaN(val) && val !== 0);
        const maxDataValue = values.length > 0 ? Math.max(...values) : undefined;
        const minDataValue = values.length > 0 ? Math.min(...values) : undefined;
        
        // Start with provided min/max values or use data min/max
        let effectiveMin = minValue !== undefined ? minValue : minDataValue;
        let effectiveMax = maxValue !== undefined ? maxValue : maxDataValue;
        
        // If we have thresholds, ensure they're included in the range
        const thresholdValues = [
            validThresholds.low, 
            validThresholds.normalLow, 
            validThresholds.normalHigh, 
            validThresholds.high
        ].filter(val => val !== undefined);
        
        if (thresholdValues.length > 0) {
            const minThreshold = Math.min(...thresholdValues);
            const maxThreshold = Math.max(...thresholdValues);
            
            // If we have threshold values, ensure they're within the range
            if (minThreshold !== undefined) {
                effectiveMin = effectiveMin !== undefined ? Math.min(effectiveMin, minThreshold) : minThreshold;
            }
            
            if (maxThreshold !== undefined) {
                effectiveMax = effectiveMax !== undefined ? Math.max(effectiveMax, maxThreshold) : maxThreshold;
            }
        }
        
        // Add buffer to make visualization nicer (10% on both ends)
        if (effectiveMin !== undefined && effectiveMax !== undefined) {
            const range = effectiveMax - effectiveMin;
            effectiveMin = Math.max(0, effectiveMin - range * 0.1);
            effectiveMax = effectiveMax + range * 0.1;
        }
        
        // For sleep data, cap at 24 hours (1440 minutes)
        if (isSleepData) {
            return {
                min: effectiveMin !== undefined ? effectiveMin : 0,
                max: effectiveMax !== undefined ? Math.min(effectiveMax, 1440) : 1440
            };
        }
        
        return {
            min: effectiveMin !== undefined ? effectiveMin : 0,
            max: effectiveMax !== undefined ? effectiveMax : 100 // Fallback if no data
        };
    };

    const thresholdRange = getFullThresholdRange();
    
    // Use threshold range directly for chart min/max
    const effectiveMinValue = thresholdRange.min;
    const effectiveMaxValue = thresholdRange.max;

    const calculateDailyStatuses = (historyData, validThresholds) => {
        // Check if we have valid data to process
        if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
            return ['No Data'];
        }

        const statusList = historyData.map(data => {
            // Handle null/undefined values or missing value property
            if (!data || data.value === null || data.value === undefined) {
                return 'No Data';
            }

            // Parse the value if it's a sleep time format (e.g., "8h 30m")
            let value = data.value;
            if (typeof value === 'string' && value.match(/^\d{1,2}h\s\d{1,2}m$/)) {
                value = parseTimeToMinutes(value);
            }

            // Evaluate against thresholds to determine status
            if (value <= validThresholds.low) return 'Caution';
            if (value <= validThresholds.normalLow) return 'Attention';
            if (value <= validThresholds.normalHigh) return 'Good';
            if (value <= validThresholds.high) return 'Attention';
            return 'Caution';
        });
        return statusList;
    }
    
    const statusList = calculateDailyStatuses(historyData, validThresholds);
    
    const calculateWeeklyStatus = (statusList) => {
        // If we have no status list or it's empty, return 'No Data'
        if (!statusList || statusList.length === 0) {
            return 'No Data';
        }
        
        // If there is not enough data, return 'Insufficient data'
        const noDataCount = statusList.filter(status => status === 'No Data').length;
        if (noDataCount >= 3 || statusList.length < 3) return 'Insufficient data';

        // Calculate weekly status based on the most common status of the week
        const weeklyStatusCounts = statusList.reduce((acc, status) => {
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        
        // If all counts are equal, prioritize in this order: Good > Attention > Caution
        const statusPriority = { 'Good': 3, 'Attention': 2, 'Caution': 1, 'No Data': 0 };
        
        const mostCommonStatus = Object.keys(weeklyStatusCounts).reduce((a, b) => {
            // If counts are the same, choose the one with higher priority
            if (weeklyStatusCounts[a] === weeklyStatusCounts[b]) {
                return statusPriority[a] > statusPriority[b] ? a : b;
            }
            // Otherwise choose the one with higher count
            return weeklyStatusCounts[a] > weeklyStatusCounts[b] ? a : b;
        });
        
        return mostCommonStatus;
    }
    
    const weeklyStatus = calculateWeeklyStatus(statusList);


    const data = {
        labels: processedData.dates,
        datasets: [
            {
                type: "line",
                label: "Trend",
                data: processedData.values,
                borderColor: "#3498db",
                borderWidth: 4,
                tension: 0.4,
                fill: false,
                pointBackgroundColor: "#3498db",
                pointRadius: 4,
                order: 1,
                datalabels: {
                    display: false
                },
                // Custom legend styling for line
                pointStyle: 'line',
                pointBorderWidth: 2
            },
            {
                label: metricLabel || "Value",
                data: processedData.values,
                type: "bar",
                backgroundColor: 'rgba(200, 200, 200, 0.7)',
                borderColor: "#4B4B4D",
                borderWidth: 1,
                order: 2,
                // Legend styling only (won't affect the actual bars)
                legendPointStyle: 'rect',
                legendBorderColor: 'black',
                legendBackgroundColor: 'transparent'
            }
        ]
    };

    // Format title based on status if available
    const chartTitle = title ? [title] : [];
    // if (validThresholds.status) {
    //     chartTitle.push(`Status: ${weeklyStatus}`);
    // }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 0,
                top: 30,
                right: 20,
                bottom: 10
            }
        },
        scales: {
            y: {
                grid: {
                    display: true
                },
                title: {
                    display: true,
                    text: metricLabel || 'Value',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                min: effectiveMinValue,
                max: effectiveMaxValue,
                suggestedMax: effectiveMaxValue,
                suggestedMin: effectiveMinValue,
                stacked: false,
                ticks: {
                    callback: function(value) {
                        return Number(value).toFixed(decimalPlaces);
                    },
                    // Add key threshold values to ensure they appear on the axis
                    includeBounds: true,
                    // Force include the threshold values
                    afterBuildTicks: function(scale) {
                        // Include both reasonable tick values and threshold values
                        const range = effectiveMaxValue - effectiveMinValue;
                        const step = range > 20 ? Math.ceil(range / 8) : range / 8;
                        const baseTickValues = [];
                        
                        // Generate reasonable tick values based on our range
                        for (let i = Math.ceil(effectiveMinValue); i <= effectiveMaxValue; i += step) {
                            baseTickValues.push(i);
                        }
                        
                        const thresholdValues = [
                            validThresholds.low,
                            validThresholds.normalLow, 
                            validThresholds.normalHigh,
                            validThresholds.high
                        ].filter(val => val !== undefined);
                        
                        // Combine and remove duplicates
                        const allValues = [...new Set([...baseTickValues, ...thresholdValues])];
                        
                        // Sort values and convert to tick objects
                        return allValues.sort((a, b) => a - b).map(value => ({ value }));
                    }
                },
                beginAtZero: false
            },
            x: {
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'Daily Reports',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    font: {
                        size: 11
                    },
                    padding: 10
                }
            }
        },
        plugins: {
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: function(value) {
                    // Format based on data type
                    if (isSleepData) {
                        return formatDuration(Math.max(0, value));
                    }
                    return Number(value).toFixed(decimalPlaces);
                },
                font: {
                    weight: 'bold',
                    size: 11
                }
            },
            legend: {
                display: true,
                position: 'top',
                align: 'center',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Body Turns Count': 'Body Turns counted',
                        'Trend': 'Trend of the metric'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Debug: Log the exact legend item text to see what we're working with
                    console.log('Legend item text:', JSON.stringify(legendItem.text));
                    console.log('Available tooltip keys:', Object.keys(tooltips));
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    } else {
                        // Fallback: Try to find a close match for the text
                        const keys = Object.keys(tooltips);
                        const closeMatch = keys.find(key => 
                            key.toLowerCase().includes('night') && 
                            key.toLowerCase().includes('sleep'));
                        
                        if (closeMatch) {
                            tooltipEl.innerHTML = tooltips[closeMatch];
                            tooltipEl.style.opacity = '1';
                            tooltipEl.style.left = event.native.pageX + 'px';
                            tooltipEl.style.top = (event.native.pageY - 28) + 'px';
                            
                            // Log that we're using a close match
                            console.log('Using close match:', closeMatch, 'for', legendItem.text);
                        }
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                },
                labels: {
                    boxWidth: 30,
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: true,
                    font: {
                        size: 12,
                        weight: '500'
                    },
                    color: '#444',
                    generateLabels: function(chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: i === 0 ? dataset.borderColor : 'transparent',
                            strokeStyle: i === 0 ? dataset.borderColor : 'black',
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            // Use custom legend style for bars, but default for line
                            pointStyle: i === 0 ? dataset.pointStyle : dataset.legendPointStyle,
                            // Use custom colors for legend only
                            borderColor: i === 0 ? dataset.borderColor : dataset.legendBorderColor,
                            backgroundColor: i === 0 ? dataset.borderColor : dataset.legendBackgroundColor
                        }));
                    }
                }
            },
            title: {
                display: true,
                text: chartTitle,
                font: {
                    size: 16,
                    weight: 'bold'
                }
            },
            thresholds: validThresholds,
            annotation: {
                annotations: {
                    // // Only add annotations for defined thresholds
                    // ...(validThresholds.low !== undefined && {
                    //     lowLine: {
                    //         type: 'line',
                    //         yMin: validThresholds.low,
                    //         yMax: validThresholds.low,
                    //         borderColor: '#FF8A8A', // Red
                    //         borderWidth: 2,
                    //         borderDash: [5, 5],
                    //         drawTime: 'beforeDatasetsDraw',
                    //         label: {
                    //             display: false
                    //         }
                    //     }
                    // }),
                    // ...(validThresholds.normalLow !== undefined && {
                    //     normalLowLine: {
                    //         type: 'line',
                    //         yMin: validThresholds.normalLow,
                    //         yMax: validThresholds.normalLow,
                    //         borderColor: '#FFE566', // Yellow
                    //         borderWidth: 2,
                    //         borderDash: [5, 5],
                    //         drawTime: 'beforeDatasetsDraw',
                    //         label: {
                    //             display: false
                    //         }
                    //     }
                    // }),
                }
            }
        }
    };

    const formatDuration = (minutes) => {
        if (isSleepData) {
            // Format as hours and minutes for sleep data
            const hours = Math.floor(minutes / 60);
            const mins = Math.floor(minutes % 60);
            return `${hours}h ${mins}m`;
        }
        // For non-sleep data, show with appropriate decimal places
        return typeof minutes === 'number' ? minutes.toFixed(decimalPlaces) : '0';
    };

    return (
        <div style={{ position: 'relative', height: '600px', width: '100%', marginTop: '30px', marginBottom: '30px'  }}>
            <div style={{ height: '100%', width: '100%' }}>
                <Chart data={data} options={options} plugins={[thresholdBackgroundPlugin]} />
            </div>
        </div>
    );
};

export default ModularTrendsSpecificThreshholdChart;
